import { Group, Select, Loader } from '@mantine/core';
import { Dispatch, SetStateAction, useMemo, useEffect } from 'react';

import { useGetInstagramFilters } from '../../api/getInstagramFilters';
import { LABELS } from '../../constants';
import { InstagramAnalyticsFiltersState } from '../../routes/Instagram';

import { DateRange } from '@/components';

type InstagramControlsProps = {
  filters: InstagramAnalyticsFiltersState;
  setFilters: Dispatch<SetStateAction<InstagramAnalyticsFiltersState>>;
};

export const InstagramControlsPanel = ({ filters, setFilters }: InstagramControlsProps) => {
  const { dates, pageId, topic, pageLink, geo } = filters;
  const [from, to] = dates;
  const dateFrom = from?.valueOf() || 0;
  const dateTo = to?.valueOf() || 0;

  const getInstagramFilters = useGetInstagramFilters({ dateFrom, dateTo });
  const filtersData = getInstagramFilters.data?.filters;

  const options = useMemo(() => {
    const pageIds = filtersData?.pageIds ?? [];
    const topics = filtersData?.topics ?? [];
    const pageLinks = filtersData?.pageLinks ?? [];
    const geos = filtersData?.geos ?? [];
    return {
      pageIds: ['ALL', ...new Set(pageIds)],
      topics: ['ALL', ...new Set(topics)],
      pageLinks: ['ALL', ...new Set(pageLinks)],
      geos: ['ALL', ...new Set(geos)],
    };
  }, [filtersData]);

  useEffect(() => {
    const actualPageIds = options.pageIds.filter((id) => id !== 'ALL');
    if (pageId !== 'ALL' && !actualPageIds.includes(pageId)) {
      setFilters((prev) => ({ ...prev, pageId: 'ALL' }));
    }
  }, [options.pageIds, pageId, setFilters]);

  useEffect(() => {
    const actualTopics = options.topics.filter((t) => t !== 'ALL');
    if (topic !== 'ALL' && !actualTopics.includes(topic)) {
      setFilters((prev) => ({ ...prev, topic: 'ALL' }));
    }
  }, [options.topics, topic, setFilters]);

  useEffect(() => {
    const actualPageLinks = options.pageLinks.filter((t) => t !== 'ALL');
    if (pageLink !== 'ALL' && !actualPageLinks.includes(pageLink)) {
      setFilters((prev) => ({ ...prev, pageLink: 'ALL' }));
    }
  }, [options.pageLinks, pageLink, setFilters]);

  useEffect(() => {
    const actualGeos = options.geos.filter((t) => t !== 'ALL');
    if (geo !== 'ALL' && !actualGeos.includes(geo)) {
      setFilters((prev) => ({ ...prev, geo: 'ALL' }));
    }
  }, [options.geos, geo, setFilters]);

  const isLoading = getInstagramFilters.isLoading;

  const selects = [
    { label: LABELS.TOPIC, key: 'topic', value: topic, options: options.topics },
    { label: LABELS.EXPERT, key: 'pageId', value: pageId, options: options.pageIds },
    { label: LABELS.PAGE, key: 'pageLink', value: pageLink, options: options.pageLinks },
    { label: LABELS.GEO, key: 'geo', value: geo, options: options.geos },
  ];

  return (
    <Group className="selectors-wrapper" justify="center" align="end" mb="xl">
      <DateRange
        label={LABELS.SELECT_DATES_RANGE}
        placeholder={LABELS.DATE_PICKER_INPUT_PLACEHOLDER}
        value={dates}
        onChange={(dates) => setFilters({ ...filters, dates })}
      />
      {selects.map(({ label, key, value, options }) => (
        <Select
          key={key}
          label={label}
          value={value}
          data={options}
          onChange={(v) => setFilters({ ...filters, [key]: v ?? 'ALL' })}
          disabled={isLoading}
          rightSection={isLoading ? <Loader size="xs" /> : null}
        />
      ))}
    </Group>
  );
};
